import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  BalanceAlert,
  SetBalanceAlertRequest,
  CreateReflinkRequest,
  UpdateReflinkRequest,
  ReferralLink,
  UserPreferencesRequest,
  Profile,
  BaseRequest,
} from '@/api';
import { ProfileData, BalanceAlertData } from '../models';

export interface ProfileDataSliceState {
  data: ProfileData;
  loading: boolean;
  loadingPreferences: boolean;
  error: string | null;
}

const initialState: ProfileDataSliceState = {
  data: null,
  loading: false,
  loadingPreferences: false,
  error: null,
};

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    setData: (state, action: PayloadAction<ProfileData>) => {
      const { data } = state;

      if (data && action.payload) {
        const { refLinksData, refLinks, balanceAlertsSets } = data;
        action.payload = {
          ...action.payload,
          refLinksData,
          refLinks,
          balanceAlertsSets,
        };
      }

      return {
        ...state,
        data: action.payload,
      };
    },
    setLoading: (state, action: PayloadAction<boolean>) => ({
      ...state,
      loading: action.payload,
    }),
    setLoadingPreferences: (state, action: PayloadAction<boolean>) => ({
      ...state,
      loadingPreferences: action.payload,
    }),
    setError: (state, action: PayloadAction<string | null>) => ({
      ...state,
      error: action.payload,
    }),
    getProfileInfo: (state) => ({
      ...state,
      error: null,
      loading: true,
    }),
    updateAlertPeriod: (
      state,
      action: PayloadAction<SetBalanceAlertRequest>,
    ) => ({
      ...state,
      error: null,
      loading: true,
    }),
    setBalanceAlertData: (state, action: PayloadAction<BalanceAlertData>) => {
      const { data } = state;
      if (data) {
        const { payload } = action;
        data.balanceAlertsSets = payload as BalanceAlert;
      }
    },
    deleteBalanceAlert: (state) => ({
      ...state,
      error: null,
      loading: true,
    }),
    fetchRefLinks: (state) => ({
      ...state,
      error: null,
      loading: true,
    }),
    createReflink: (state, action: PayloadAction<CreateReflinkRequest>) => ({
      ...state,
      error: null,
      loading: true,
    }),
    updateReflink: (state, action: PayloadAction<UpdateReflinkRequest>) => ({
      ...state,
      error: null,
      loading: true,
    }),
    setRefLinksData: (state, action: PayloadAction<ReferralLink[]>) => {
      const { data } = state;
      if (data) {
        const { payload } = action;
        data.refLinksData = payload;
        data.refLinks = payload.map((item) => item.link);
      }
    },
    monitorUser: (state) => state,
    updatePreferences: (
      state,
      action: PayloadAction<{
        payload: UserPreferencesRequest;
        callback?: () => void;
      }>,
    ) => ({
      ...state,
      error: null,
      loadingPreferences: true,
    }),
    setPreferences: (state, action: PayloadAction<Record<any, any>>) => {
      const { data } = state;
      if (data) {
        const { payload } = action;
        data.preferences = payload;
      }
    },
    setWebhookUrl: (state, action: PayloadAction<string>) => {
      const { data } = state;
      if (data) {
        const { payload } = action;
        data.webhookUrl = payload;
      }
    },
    pickUsage: (state, action: PayloadAction<BaseRequest>) => ({
      ...state,
      error: null,
      loadingPreferences: true,
    }),
    updateAttribute: (state, action: PayloadAction<Partial<Profile>>) => {
      const { data } = state;

      if (data) {
        const { payload } = action;
        const keys = Object.keys(payload);

        keys.forEach((key) => {
          const attribute = key as keyof ProfileData;
          data[attribute] = payload[attribute];
        });
      }
    },
  },
});

export { profileSlice };
